import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const ComboBox = ({
  allowDeselect = true,
  defaultValue,
  disabled = false,
  error,
  id,
  isRequired = false,
  label,
  name,
  onValueChange,
  options,
  placeholder = 'Select...',
  value: valueProp
}: {
  allowDeselect?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  error?: ReactNode;
  id: string;
  isRequired?: boolean;
  label: string;
  name: string;
  onValueChange?: (value: string) => void;
  options: {
    label: string;
    value: string;
  }[];
  placeholder?: string;
  value?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');

  useEffect(() => {
    if (valueProp !== undefined) {
      return;
    }

    setInternalValue(defaultValue ?? '');
  }, [defaultValue, valueProp]);

  const value = valueProp ?? internalValue;

  const selectedOption = options.find(option => option.value === value);

  return (
    <Field data-invalid={!!error}>
      <FieldLabel
        className={isRequired ? 'inline-block' : undefined}
        htmlFor={id}
      >
        {label}
        {isRequired && (
          <>
            {' '}
            <span className="text-red-500">*</span>
          </>
        )}
      </FieldLabel>
      <input
        name={name}
        type="hidden"
        value={value}
      />
      <Popover
        onOpenChange={nextOpen => {
          if (disabled) {
            return;
          }

          setOpen(nextOpen);
        }}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
            id={id}
            role="combobox"
            type="button"
            variant="outline"
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronsUpDownIcon className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput
              className="h-9"
              placeholder={`Search ${label}...`}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    onSelect={currentValue => {
                      const nextValue =
                        currentValue === value && allowDeselect
                          ? ''
                          : currentValue;

                      if (valueProp === undefined) {
                        setInternalValue(nextValue);
                      }

                      onValueChange?.(nextValue);
                      setOpen(false);
                    }}
                    value={option.value}
                  >
                    {option.label}
                    <CheckIcon
                      className={cn(
                        'ml-auto',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError>{error}</FieldError>
    </Field>
  );
};

export default ComboBox;
